import {
  DataBlockVariant,
  AppointmentColorSettingsRuleDto,
  DataBlockDictionaryItemDto,
  ResourceDto,
  OptionalSubtenantDictionaryRecordDto,
  ResourceType,
  FileInfoDto,
  QueryFactory,
  DoctorType,
  InputTemplateType,
} from '@lmt/shared-types';
import { CreateAbsence } from '@lmt/unicorn-shared/app/components/absences/absence-form/CreateAbsence';
import { CreateCalendarConfiguration } from '@lmt/unicorn-shared/app/components/calendar/configuration/CreateCalendarConfiguration';
import { DoctorCreatingOrEditingForm } from '@lmt/unicorn-shared/app/components/doctors/doctor-form/DoctorCreatingOrEditingForm';
import { SelectVisitsPopup } from '@lmt/unicorn-shared/app/components/documents/expressions/SelectVisitsPopup';
import { GDTMappingListPage } from '@lmt/unicorn-shared/app/components/gdt/mappings/list/GDTMappingListPage';
import { HL7FilterEditForm } from '@lmt/unicorn-shared/app/components/hl7/filters/components/HL7FilterEditForm';
import { MergeInsuranceCardData } from '@lmt/unicorn-shared/app/components/merge-assistant/form/MergeInsuranceCardData';
import { MergePatientWithCardData } from '@lmt/unicorn-shared/app/components/merge-assistant/form/MergePatientWithCardData';
import { PatientListConfigurationData } from '@lmt/unicorn-shared/app/components/patients/list/common/types';
import { CreatePatientListConfiguration } from '@lmt/unicorn-shared/app/components/patients/list/configuration/CreatePatientListConfiguration';
import { VersionsListFormWrapper } from '@lmt/unicorn-shared/app/components/patients/profile/cases/components/documents/VersionsListFormWrapper';
import { CreateOrEditResource } from '@lmt/unicorn-shared/app/components/resources/create/CreateOrEditResource';
import { ConfigureDictionary } from '@lmt/unicorn-shared/app/components/settings/CustomDictionary/configure/ConfigureDictionary';
import { CreateOrEditDictionaryItem } from '@lmt/unicorn-shared/app/components/settings/CustomDictionary/edit/CreateOrEditDictionaryItem';
import { ChangeLanguage } from '@lmt/unicorn-shared/app/components/users/change-language/ChangeLanguage';
import { ChangeOwnPassword } from '@lmt/unicorn-shared/app/components/users/change-password/ChangeOwnPassword';
import { SetPassword } from '@lmt/unicorn-shared/app/components/users/change-password/SetPassword';
import { CreateOrEditVisitType } from '@lmt/unicorn-shared/app/components/visit-types/form/CreateOrEditVisitType';
import { notifyFormComplete } from '@lmt/unicorn-shared/app/features/global-forms/operations';
import { useUniDispatch } from '@lmt/unicorn-shared/app/store/types';
import { useModal } from '@lmt/unicorn-shared/app/components/common/ui-kit/modal/useModal';
import { QuerySuspenseErrorWrapper } from '@lmt/unicorn-shared/app/helpers/retry-helper';
import { ViewType } from 'devextreme/ui/scheduler';
import { CardData } from '@lmt/unicorn-shared/app/components/card-reader/components/CardReaderHandler';
import React, { FC, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ColorRuleEditForm } from '../calendar/color-settings/form/ColorRuleEditForm';
import { DictionaryInfo } from '../dictionaries/dictionaries.types';
import { CreateOrEditDictionaryRecord } from '@lmt/unicorn-shared/app/components/dictionaries/dictionary-record-form/CreateOrEditDictionaryRecord';
import { CreateDataBlockField } from '../measurement-fields/CreateDataBlockField';
import { CreateDataBlockType } from '../measurement-types/CreateDataBlockType';
import { CreateDataBlock } from '../measurements/CreateDataBlock';
import DataBlockView from '../measurements/DataBlockView';
import { CreatePatient } from '../patients/patient-form/CreatePatient';
import { EditPatient } from '../patients/patient-form/EditPatient';
import { ImportDocument } from '../patients/profile/cases/components/documents/ImportDocument';
import { RoleForm } from '../roles/role-form/RoleForm';
import { CreateOrEditSubtenant } from '@lmt/unicorn-shared/src/app/components/subtenants/create/CreateOrEditSubtenant';
import { CreateUser } from '../users/create/CreateUser';
import { EditUser } from '../users/edit/EditUser';
import {
  defaultForm,
  expandedForm,
  expandedOnlyForm,
  Form,
  FormComponentProps,
  FormType,
  normalForm,
  StateSettings,
  WindowState,
} from './GlobalForms.types';
import { PopupWindow } from './popup/PopupWindow';
import { FormsContext, GlobalFormsContext } from './useGlobalForms';
import MiniScreenImporter from '../miniscreen-importer/MiniScreenImporter';
import { ImportMeasurementFiles } from '../miniscreen-importer/components/ImportMeasurementFiles';
import { ImportGDTFile } from '../gdt/import/ImportGdtFile';
import { SendViaGDT } from '../patients/profile/cases/components/blocks/SendViaGDT';

import styles from './GlobalForms.module.scss';
import { CreateOrEditNote } from '../calendar/notes/form/NoteCreatingOrEditingForm';
import DataBlockStatistic from '../measurements/DataBlockStatistic';
import { DataBlockValues } from '../measurements/common';
import CreateDataBlockStatistic from '../measurements/CreateDataBlockStatistic';
import clsx from 'clsx';
import { QueryBuilderForm } from '../documents/components/QueryBuilderForm';
import { EventCreationForm } from '../calendar/EventCreationForm';
import { MergePair } from '../merge-assistant/form/MergePair';
import { ReImportMeasurementsForm } from '../mappings/components/ReImportMeasurementsForm';
import { CopyDataBlockFieldValueForm } from '../measurements/copy-datablock-field/CopyDataBlockFieldValueForm';
import { CreatePrefixForm } from '../documents/components/CreatePrefixForm';
import { useEventCallback } from '@mui/material';
import { InputTemplateForm } from '../input-templates/form/InputTemplateForm';
import { SendTestMessageForm } from '../tenant-settings/components/mail/SendTestMessageForm';
import { MovePatientsForm } from '../subtenants/list/components/MovePatientsForm';

export const GlobalForms: FC<React.PropsWithChildren> = (props) => {
  const dispatch = useUniDispatch();
  const modals = useModal();
  const i18n = useTranslation();
  const settingsQuery = QueryFactory.GeneralSettingsQuery.useGetQuery({
    throwOnError: false,
  });

  const formComponents: {
    [formType in FormType]: {
      title: string;
      componentType: () => React.ComponentType<FormComponentProps & any>;
    };
  } = useMemo(
    () => ({
      createPatient: {
        title: i18n.t('global_forms.create_patient'),
        componentType: () => CreatePatient,
      },
      editPatient: {
        title: i18n.t('global_forms.edit_patient'),
        componentType: () => EditPatient,
      },
      changeOwnPassword: {
        title: i18n.t('global_forms.change_password'),
        componentType: () => ChangeOwnPassword,
      },
      changeLanguage: {
        title: i18n.t('global_forms.change_language'),
        componentType: () => ChangeLanguage,
      },
      setPassword: {
        title: i18n.t('global_forms.set_password'),
        componentType: () => SetPassword,
      },
      createVisit: {
        title: i18n.t('global_forms.create_visit'),
        componentType: () => EventCreationForm,
      },
      createVisitWithResources: {
        title: i18n.t('global_forms.create_visit'),
        componentType: () => EventCreationForm,
      },
      editVisit: {
        title: i18n.t('global_forms.edit_visit'),
        componentType: () => EventCreationForm,
      },
      createMeeting: {
        title: i18n.t('global_forms.create_meeting'),
        componentType: () => EventCreationForm,
      },
      createEvent: {
        title: i18n.t('global_forms.create_event'),
        componentType: () => EventCreationForm,
      },
      createUser: {
        title: i18n.t('global_forms.create_user'),
        componentType: () => CreateUser,
      },
      editUser: {
        title: i18n.t('global_forms.edit_user'),
        componentType: () => EditUser,
      },
      createRole: {
        title: i18n.t('global_forms.create_role'),
        componentType: () => RoleForm,
      },
      editRole: {
        title: i18n.t('global_forms.edit_role'),
        componentType: () => RoleForm,
      },
      createSubtenant: {
        title: i18n.t('global_forms.create_subtenant'),
        componentType: () => CreateOrEditSubtenant,
      },
      editSubtenant: {
        title: i18n.t('global_forms.edit_subtenant'),
        componentType: () => CreateOrEditSubtenant,
      },
      importFromMiniscreen: {
        title: i18n.t('global_forms.import_from_miniscreen'),
        componentType: () => MiniScreenImporter,
      },
      createDoctor: {
        title: i18n.t('global_forms.create_doctor'),
        componentType: () => DoctorCreatingOrEditingForm,
      },
      editDoctor: {
        title: i18n.t('global_forms.edit_doctor'),
        componentType: () => DoctorCreatingOrEditingForm,
      },
      createDataBlockType: {
        title: i18n.t('global_forms.create_data_block_type'),
        componentType: () => CreateDataBlockType,
      },
      createDataBlockField: {
        title: i18n.t('global_forms.create_data_block_field'),
        componentType: () => CreateDataBlockField,
      },
      editDataBlockField: {
        title: i18n.t('global_forms.edit_data_block_field'),
        componentType: () => CreateDataBlockField,
      },
      editDataBlock: {
        title: i18n.t('global_forms.view_data_block'),
        componentType: () => DataBlockView,
      },
      createDataBlock: {
        title: i18n.t('global_forms.create_data_block'),
        componentType: () => CreateDataBlock,
      },
      hl7Filter: {
        title: i18n.t('global_forms.hl7filter'),
        componentType: () => HL7FilterEditForm,
      },
      appointmentColorSettings: {
        title: i18n.t('global_forms.appointment_color_settings'),
        componentType: () => ColorRuleEditForm,
      },
      createResource: {
        title: i18n.t('global_forms.create_resource'),
        componentType: () => CreateOrEditResource,
      },
      editResource: {
        title: i18n.t('global_forms.edit_resource'),
        componentType: () => CreateOrEditResource,
      },
      importDocument: {
        title: i18n.t('global_forms.import_document'),
        componentType: () => ImportDocument,
      },
      importMeasurementFiles: {
        title: i18n.t('global_forms.import_measurement_files'),
        componentType: () => ImportMeasurementFiles,
      },
      importGDTFile: {
        title: i18n.t('global_forms.import_gdt_file'),
        componentType: () => ImportGDTFile,
      },
      mergeField: {
        title: i18n.t('global_forms.merge_field'),
        componentType: () => QueryBuilderForm,
      },
      configureCustomDictionary: {
        title: i18n.t('global_forms.configure_dictionary'),
        componentType: () => ConfigureDictionary,
      },
      createCustomDictionaryItem: {
        title: i18n.t('global_forms.create_custom_dictionary_item'),
        componentType: () => CreateOrEditDictionaryItem,
      },
      editCustomDictionaryItem: {
        title: i18n.t('global_forms.edit_custom_dictionary_item'),
        componentType: () => CreateOrEditDictionaryItem,
      },
      createDictionaryRecord: {
        title: i18n.t('global_forms.create_dictionary_record'),
        componentType: () => CreateOrEditDictionaryRecord,
      },
      editDictionaryRecord: {
        title: i18n.t('global_forms.edit_dictionary_record'),
        componentType: () => CreateOrEditDictionaryRecord,
      },
      createCalendarConfiguration: {
        title: i18n.t('global_forms.create_calendar_configuration'),
        componentType: () => CreateCalendarConfiguration,
      },
      editCalendarConfiguration: {
        title: i18n.t('global_forms.edit_calendar_configuration'),
        componentType: () => CreateCalendarConfiguration,
      },
      createPatientListConfiguration: {
        title: i18n.t('global_forms.create_patient_list_configuration'),
        componentType: () => CreatePatientListConfiguration,
      },
      editPatientListConfiguration: {
        title: i18n.t('global_forms.edit_patient_list_configuration'),
        componentType: () => CreatePatientListConfiguration,
      },
      mergePair: {
        title: i18n.t('global_forms.merge_pair'),
        componentType: () => MergePair,
      },
      manageGDTMappings: {
        title: i18n.t('global_forms.manage_gdt_mappings'),
        componentType: () => GDTMappingListPage,
      },
      selectVisits: {
        title: i18n.t('global_forms.select_visits'),
        componentType: () => SelectVisitsPopup,
      },
      sendViaGDT: {
        title: i18n.t('global_forms.send_via_gdt'),
        componentType: () => SendViaGDT,
      },
      createAbsence: {
        title: i18n.t('global_forms.create_absence'),
        componentType: () => CreateAbsence,
      },
      editAbsence: {
        title: i18n.t('global_forms.edit_absence'),
        componentType: () => CreateAbsence,
      },
      createNote: {
        title: i18n.t('global_forms.create_note'),
        componentType: () => CreateOrEditNote,
      },
      editNote: {
        title: i18n.t('global_forms.edit_note'),
        componentType: () => CreateOrEditNote,
      },
      fileVersionHistory: {
        title: i18n.t('global_forms.version_history'),
        componentType: () => VersionsListFormWrapper,
      },
      editDataBlockStatistic: {
        title: i18n.t('global_forms.edit_data_block_statistic'),
        componentType: () => DataBlockStatistic,
      },
      createDataBlockStatistic: {
        title: i18n.t('global_forms.edit_data_block_statistic'),
        componentType: () => CreateDataBlockStatistic,
      },
      createVisitType: {
        title: i18n.t('global_forms.create_visit_type'),
        componentType: () => CreateOrEditVisitType,
      },
      editVisitType: {
        title: i18n.t('global_forms.edit_visit_type'),
        componentType: () => CreateOrEditVisitType,
      },
      editInputTemplates: {
        title: i18n.t('global_forms.edit_input_templates'),
        componentType: () => InputTemplateForm,
      },
      reimportMeasurements: {
        title: i18n.t('global_forms.reimport_measurements'),
        componentType: () => ReImportMeasurementsForm,
      },
      movePatients: {
        title: i18n.t('global_forms.move_patients'),
        componentType: () => MovePatientsForm,
      },
      mergePatientWithCardData: {
        title: i18n.t('global_forms.merge_patient_with_card_data'),
        componentType: () => MergePatientWithCardData,
      },
      mergeInsuranceCardData: {
        title: i18n.t('global_forms.merge_insurance_card_data'),
        componentType: () => MergeInsuranceCardData,
      },
      copyDataBlockFieldValue: {
        title: i18n.t('global_forms.copy_datablock_field_value'),
        componentType: () => CopyDataBlockFieldValueForm,
      },
      createCodePrefix: {
        title: i18n.t('global_forms.create_code_prefix'),
        componentType: () => CreatePrefixForm,
      },
      sendTestMessage: {
        title: i18n.t('global_forms.send_test_message'),
        componentType: () => SendTestMessageForm,
      },
    }),
    [i18n.i18n.language],
  );

  const [openedForms, setOpenedForms] = useState<{ [formId: number]: Form }>(
    {},
  );

  const updateForm = useEventCallback(
    (formId: number, copyForm: boolean, action: (form: Form) => void) => {
      setOpenedForms((currentlyOpenedForms) => {
        let newOpenedForms = currentlyOpenedForms;
        const form = currentlyOpenedForms[formId];
        if (form) {
          const updatedForm = copyForm ? Object.assign({}, form) : form;
          action(updatedForm);
          newOpenedForms = copyForm
            ? Object.assign({}, currentlyOpenedForms)
            : currentlyOpenedForms;
          newOpenedForms[formId] = updatedForm;
        }
        return newOpenedForms;
      });
    },
  );

  const changeFormState = useEventCallback(
    (formId: number, state: WindowState) => {
      updateForm(formId, true, (form) => (form.state = state));
    },
  );

  const expandForm = useEventCallback((formId: number) => {
    changeFormState(formId, 'expanded');
  });

  const changeFormTitle = useEventCallback((formId: number, title: string) => {
    updateForm(formId, true, (form) => (form.title = title));
    //changeFormState(formId, 'expanded');
  });

  const setFormIsHidden = useEventCallback(
    (formId: number, isHidden: boolean) => {
      updateForm(formId, true, (form) => (form.isHidden = isHidden));
    },
  );

  const setIsCloseButtonHidden = useEventCallback(
    (formId: number, isCloseButtonHidden: boolean) => {
      updateForm(
        formId,
        true,
        (form) => (form.isCloseButtonHidden = isCloseButtonHidden),
      );
    },
  );

  const registerFormDirtyStateRetriever = useEventCallback(
    (formId: number, retriever: () => boolean) => {
      updateForm(
        formId,
        false,
        (form) => (form.formDirtyStateRetriever = retriever),
      );
    },
  );

  const lastFormIdentifier = useRef<number>(1);
  const openForm = useEventCallback(
    (
      formType: FormType,
      stateSettings?: StateSettings,
      additionalFormProps?: { [key in string]: unknown },
      title?: string,
      resolve?: (result: any | undefined) => void,
      closeExistingForms?: boolean,
    ): number | undefined => {
      const openedFormsInternal = openedForms;
      if (closeExistingForms) {
        const existingForms = Object.values(openedFormsInternal).filter(
          (form) => form.type === formType,
        );
        existingForms.forEach((form) => closeForm(form.id));
      } else if (stateSettings?.isExpanded) {
        // if form is expanded we do not let open another one
        if (
          Object.values(openedFormsInternal).some(
            (form) => form.type === formType,
          )
        ) {
          return undefined;
        }
      }
      const newForm: Form = {
        id: lastFormIdentifier.current++,
        type: formType,
        stateSettings: stateSettings ?? defaultForm,
        additionalFormProps,
        resolve,
        state: (stateSettings ?? defaultForm).isNormal ? 'normal' : 'expanded',
        isHidden: false,
        title: title,
        isCloseButtonHidden: false,
      };
      setOpenedForms((currentlyOpenedForms) =>
        Object.assign({ [newForm.id]: newForm }, currentlyOpenedForms),
      );
      return newForm.id;
    },
  );

  const isAnyImportFormOpened = useMemo(() => {
    return Object.values(openedForms).some(
      (form) => form.type === 'importFromMiniscreen',
    );
  }, [openedForms]);

  const resolveForm = useEventCallback(
    (form: Form, result: any | undefined) => {
      const formResolve = form.resolve;
      if (formResolve) {
        formResolve(result);
        delete form.resolve;
      }
    },
  );

  const closeForm = useEventCallback((formId: number) => {
    setOpenedForms((currentlyOpenedForms) => {
      let newOpenedForms = currentlyOpenedForms;
      const formToClose = currentlyOpenedForms[formId];
      if (formToClose) {
        resolveForm(formToClose, undefined);
        newOpenedForms = { ...currentlyOpenedForms };
        delete newOpenedForms[formId];
      }
      return newOpenedForms;
    });
  });

  const tryCloseForm = useEventCallback(async (formId: number) => {
    const form = openedForms[formId];
    if (form.formDirtyStateRetriever?.()) {
      const confirmed = await modals.showConfirm({
        title: i18n.t('global_forms.confirm_close_of_form.title'),
        text: i18n.t('global_forms.confirm_close_of_form.text'),
        okButtonText: i18n.t('global_forms.confirm_close_of_form.ok'),
        cancelButtonText: i18n.t('global_forms.confirm_close_of_form.cancel'),
      });
      if (confirmed) {
        closeForm(formId);
      }
    } else {
      closeForm(formId);
    }
  });

  const loadBlocking = useEventCallback(
    async (formId: number, title: string, load: () => Promise<any>) => {
      updateForm(formId, true, (form) => {
        form.isLoading = true;
        form.loadingTitle = title;
      });
      try {
        return await load();
      } finally {
        updateForm(formId, true, (form) => (form.isLoading = false));
      }
    },
  );

  const tryCompleteForm = useEventCallback(
    async (
      formType: FormType,
      formId: number,
      submitProcess: () => Promise<any>,
      title?: string,
    ) => {
      updateForm(formId, true, (form) => (form.isBeingCompleted = true));
      try {
        const result = await loadBlocking(formId, title || '', submitProcess);
        updateForm(formId, false, (form) => resolveForm(form, result));
        closeForm(formId);
        notifyFormComplete(formType)(dispatch);
      } finally {
        updateForm(formId, true, (form) => (form.isBeingCompleted = false));
      }
    },
  );

  const focusedFormId = useMemo<number | undefined>(() => {
    const focusedForm = Object.values(openedForms)
      .reverse()
      .find((form) => form.state === 'expanded');
    return focusedForm?.id;
  }, [openedForms]);

  const formsContext: GlobalFormsContext = useMemo(
    () => ({
      closeForm: closeForm,
      tryCloseForm: tryCloseForm,
      openPatientCreating: (
        subtenantId: number | undefined,
        doctorId: number | undefined,
        expandedOnly?: boolean,
        defaultValues?: any,
        closeExistingForms?: boolean,
      ) => {
        return new Promise<any>((resolve) =>
          openForm(
            'createPatient',
            expandedOnly
              ? expandedOnlyForm
              : settingsQuery?.data?.isPatientCreatingShortedFormEnabled
                ? defaultForm
                : expandedForm,
            {
              selectedSubtenantId: subtenantId,
              selectedDoctorId: doctorId,
              defaultValues,
            },
            undefined,
            resolve,
            closeExistingForms,
          ),
        );
      },
      openPatientEditing: (patientId: number) =>
        openForm('editPatient', expandedOnlyForm, { patientId }),
      changeOwnPassword: () =>
        openForm('changeOwnPassword', expandedOnlyForm, {}),
      changeLanguage: () => openForm('changeLanguage', expandedOnlyForm, {}),
      setUserPassword: (userId: string) =>
        openForm('setPassword', expandedOnlyForm, { userId }),
      openVisitCreating: (
        selectedInterval?: Interval,
        assignedResources?: number[],
        patientId?: number,
        caseId?: number,
        visitTypeId?: number,
        timeSlotTimeStepInMinutes?: number,
        calendarType?: ViewType,
        defaultVisitStartTimeInSeconds?: number,
      ) => {
        openForm(
          'createVisit',
          assignedResources
            ? expandedOnlyForm
            : settingsQuery?.data?.isVisitCreatingShortedFormEnabled
              ? defaultForm
              : expandedForm,
          {
            selectedInterval,
            assignedResources,
            selectedEventType: 'Visit',
            patientId,
            caseId,
            visitTypeId,
            timeSlotTimeStepInMinutes,
            calendarType,
            defaultVisitStartTimeInSeconds,
          },
        );
      },
      openVisitEditing: (
        visitId: number,
        isWeekView?: boolean,
        defaultVisitStartTimeInSeconds?: number,
      ) =>
        openForm('editVisit', expandedOnlyForm, {
          visitId,
          selectedEventType: 'Visit',
          isWeekView,
          defaultVisitStartTimeInSeconds,
        }),
      openEventCreating: (selectedInterval?: Interval, patientId?: number) =>
        openForm('createEvent', defaultForm, { selectedInterval, patientId }),
      openUserCreating: () => openForm('createUser', expandedOnlyForm),
      openUserEditing: (userId: string) =>
        openForm('editUser', expandedOnlyForm, { userId }),
      openRoleCreating: () => openForm('createRole', expandedOnlyForm),
      openRoleEditing: (roleId: number, isDefault?: boolean) =>
        openForm(
          'editRole',
          expandedOnlyForm,
          { roleId },
          isDefault
            ? i18n.t('global_forms.view_role')
            : i18n.t('global_forms.edit_role'),
        ),
      openSubtenantCreating: () =>
        openForm('createSubtenant', expandedOnlyForm),
      openSubtenantEditing: (id: number) =>
        openForm('editSubtenant', expandedOnlyForm, { id }),
      importFromMiniscreen: () => {
        if (!isAnyImportFormOpened) {
          openForm('importFromMiniscreen', normalForm);
        }
      },
      openDoctorCreating: (
        doctorType: DoctorType,
        showResourceInfo?: boolean,
      ) => {
        return new Promise<any>((resolve) =>
          openForm(
            'createDoctor',
            expandedOnlyForm,
            { doctorType, showResourceInfo },
            doctorType === DoctorType.Clinic
              ? i18n.t('global_forms.create_attending_doctor')
              : i18n.t('global_forms.create_family_doctor'),
            resolve,
          ),
        );
      },
      openDoctorEditing: (
        doctorType: DoctorType,
        doctorId: number,
        showResourceInfo?: boolean,
      ) =>
        openForm(
          'editDoctor',
          expandedOnlyForm,
          {
            doctorType,
            doctorId,
            showResourceInfo,
          },
          doctorType === DoctorType.Clinic
            ? i18n.t('global_forms.edit_attending_doctor')
            : i18n.t('global_forms.edit_family_doctor'),
        ),
      openDataBlockTypeCreation: (
        variant: DataBlockVariant,
        onSuccess?: (dataBlockTypeId: number) => void,
      ) =>
        openForm('createDataBlockType', expandedOnlyForm, {
          variant,
          onSuccess,
        }),
      openDataBlockFieldCreation: (dataBlockTypeId: number, data) =>
        openForm('createDataBlockField', expandedOnlyForm, {
          dataBlockTypeId,
          data,
        }),
      openDataBlockFieldEdit: (dataBlockFieldId: number) =>
        openForm('editDataBlockField', expandedOnlyForm, { dataBlockFieldId }),
      openDataBlock: (
        dataBlockId: number,
        dataBlockTypeId: number,
        patientId: number,
        dataBlockTypeName?: string,
      ) =>
        openForm(
          'editDataBlock',
          expandedForm,
          {
            dataBlockId,
            dataBlockTypeId,
            patientId,
          },
          dataBlockTypeName,
        ),
      openDataBlockStatisticEditing: (
        dataBlockId: number,
        dataBlockTypeId: number,
        dataBlockTypeName?: string,
      ) =>
        openForm(
          'editDataBlockStatistic',
          expandedForm,
          {
            dataBlockId,
            dataBlockTypeId,
          },
          dataBlockTypeName,
        ),
      openDataBlockStatisticCreating(
        dataBlockTypeId: number,
        patientId: number,
        caseId: number,
        visitId?: number,
        dataBlockTypeName?: string,
      ): Promise<DataBlockValues | null> {
        return new Promise<DataBlockValues | null>((resolve) => {
          openForm(
            'createDataBlockStatistic',
            expandedForm,
            {
              dataBlockTypeId,
              patientId,
              caseId,
              visitId,
            },
            dataBlockTypeName,
            (result) => resolve(result),
          );
        });
      },
      openDataBlockCreation: (
        dataBlockTypeId: number,
        patientId: number,
        caseId: number,
        visitId?: number,
        dataBlockTypeName?: string,
      ) =>
        openForm(
          'createDataBlock',
          expandedOnlyForm,
          {
            dataBlockTypeId,
            patientId,
            caseId,
            visitId,
          },
          dataBlockTypeName,
        ),
      openAppointmentColorSettings: (rule?: AppointmentColorSettingsRuleDto) =>
        openForm('appointmentColorSettings', expandedOnlyForm, {
          rule: rule,
        }),
      openHL7Filter: (filterId?: number) =>
        openForm('hl7Filter', expandedOnlyForm, { filterId }),
      openResourceCreating(type: ResourceType, title?: string) {
        openForm(
          'createResource',
          expandedOnlyForm,
          { resourceType: type },
          title,
        );
      },
      openResourceEditing(resourceToEdit: ResourceDto, title?: string) {
        openForm(
          'editResource',
          expandedOnlyForm,
          {
            resourceType: resourceToEdit.type,
            resourceToEdit,
          },
          title,
        );
      },
      importDocument(blockId: number) {
        openForm('importDocument', expandedOnlyForm, { blockId });
      },
      importMeasurementFiles() {
        openForm('importMeasurementFiles', expandedOnlyForm);
      },
      importGDTFile() {
        openForm('importGDTFile', expandedOnlyForm);
      },
      addMergeField() {
        openForm('mergeField', expandedOnlyForm, {});
      },
      editMergeField(mergeFieldId: number) {
        openForm('mergeField', expandedOnlyForm, {
          mergeFieldId: mergeFieldId,
        });
      },
      openDictionaryRecordCreating(dictionary: DictionaryInfo) {
        openForm('createDictionaryRecord', expandedOnlyForm, {
          dictionary: dictionary,
        });
      },
      openDictionaryRecordEditing(
        dictionary: DictionaryInfo,
        data: OptionalSubtenantDictionaryRecordDto,
      ) {
        openForm('editDictionaryRecord', expandedOnlyForm, {
          dictionary: dictionary,
          data: data,
        });
      },
      openConfigureCustomDictionary(dictionaryId: number) {
        openForm('configureCustomDictionary', expandedOnlyForm, {
          dictionaryId,
        });
      },
      openCreateCustomDictionaryItem(dictionaryId: number) {
        return new Promise<any>((resolve) =>
          openForm(
            'createCustomDictionaryItem',
            expandedOnlyForm,
            {
              dictionaryId,
            },
            undefined,
            resolve,
          ),
        );
      },
      openEditCustomDictionaryItem(
        dictionaryId: number,
        item: DataBlockDictionaryItemDto,
      ) {
        openForm('editCustomDictionaryItem', expandedOnlyForm, {
          dictionaryId,
          item,
        });
      },
      openCalendarConfigurationCreating() {
        return new Promise<any>((resolve) =>
          openForm(
            'createCalendarConfiguration',
            expandedOnlyForm,
            undefined,
            undefined,
            resolve,
          ),
        );
      },
      openCalendarConfigurationEditing(configurationId: number) {
        return new Promise<any>((resolve) =>
          openForm(
            'editCalendarConfiguration',
            expandedOnlyForm,
            {
              configurationId,
            },
            undefined,
            resolve,
          ),
        );
      },
      openPatientListConfigurationCreating(
        values?: Partial<PatientListConfigurationData>,
        resolve?: (newConfigurationId: number) => void,
      ) {
        openForm(
          'createPatientListConfiguration',
          expandedOnlyForm,
          {
            values,
          },
          undefined,
          resolve,
        );
      },
      openPatientListConfigurationEditing(
        configurationId?: number,
        values?: Partial<PatientListConfigurationData>,
        resolve?: (newConfigurationId: number) => void,
      ) {
        openForm(
          'editPatientListConfiguration',
          expandedOnlyForm,
          {
            configurationId,
            values,
          },
          undefined,
          resolve,
        );
      },
      openMergePair(
        firstPatientId?: number,
        secondPatientId?: number,
        patientDuplicatePairId?: number,
      ) {
        openForm('mergePair', expandedForm, {
          firstPatientId,
          secondPatientId,
          patientDuplicatePairId,
        });
      },
      openGDTMappings: () => openForm('manageGDTMappings', expandedOnlyForm),
      openGDTSending(blockId: number) {
        openForm('sendViaGDT', expandedOnlyForm, { blockId });
      },
      openSelectVisits(caseId: number): Promise<number[] | null> {
        return new Promise<number[] | null>((resolve) => {
          openForm(
            'selectVisits',
            expandedOnlyForm,
            {
              caseId,
              onVisitsSelected: resolve,
            },
            undefined,
            (result) => resolve([]),
          );
        });
      },
      openAbsenceCreating: (selectedInterval?: Interval) =>
        openForm('createAbsence', expandedOnlyForm, {
          selectedInterval,
        }),
      openAbsenceEditing: (absenceId: number) =>
        openForm('editAbsence', expandedOnlyForm, {
          absenceId,
        }),
      openNoteCreating: () => openForm('createNote', expandedOnlyForm),
      openNoteEditing: (noteId: number) =>
        openForm('editNote', expandedOnlyForm, {
          noteId,
        }),
      openFileVersionHistory: (
        groupId: string,
        expanded?: boolean,
        onVersionClick?: (file: FileInfoDto) => void,
        highlightCurrentVersion?: boolean,
      ) =>
        openForm(
          'fileVersionHistory',
          expanded ? expandedOnlyForm : normalForm,
          {
            groupId,
            onVersionClick,
            highlightCurrentVersion,
          },
        ),
      openVisitTypeCreating: () => {
        return new Promise<any>((resolve) =>
          openForm(
            'createVisitType',
            expandedOnlyForm,
            undefined,
            undefined,
            resolve,
          ),
        );
      },
      openVisitTypeEditing: (id: number) =>
        openForm('editVisitType', expandedOnlyForm, {
          id,
        }),
      openInputTemplatesEditing: (
        templateType: InputTemplateType,
        fieldId: number | null,
        isNumber: boolean,
        subtenantId?: number,
        title?: string,
      ) =>
        openForm(
          'editInputTemplates',
          expandedOnlyForm,
          {
            templateType,
            fieldId,
            isNumber,
            subtenantId,
          },
          title,
        ),
      openReImportMeasurements: (processId: string) =>
        openForm('reimportMeasurements', expandedOnlyForm, {
          processId,
        }),
      openMovePatients: (processId: string) => {
        return new Promise<any>((resolve) =>
          openForm(
            'movePatients',
            expandedOnlyForm,
            {
              processId,
            },
            undefined,
            resolve,
          ),
        );
      },
      openMergePatientWithCardData: (
        patient,
        cardData,
        closeExistingForms?: boolean,
      ) =>
        openForm(
          'mergePatientWithCardData',
          expandedOnlyForm,
          {
            patient,
            cardData,
          },
          undefined,
          undefined,
          closeExistingForms,
        ),
      openMergeInsuranceCardData(
        healthInsuranceCompany: DataBlockDictionaryItemDto,
        cardData: CardData,
      ) {
        return new Promise<any>((resolve) =>
          openForm(
            'mergeInsuranceCardData',
            expandedOnlyForm,
            {
              healthInsuranceCompany,
              cardData,
            },
            undefined,
            resolve,
          ),
        );
      },
      openCopyDataBlockValue(
        patientId: number,
        typeId: number,
        fieldId: number,
        config: string,
        typeName?: string,
      ): Promise<string | undefined> {
        return new Promise<string | undefined>((resolve) => {
          openForm(
            'copyDataBlockFieldValue',
            expandedOnlyForm,
            {
              patientId,
              typeId,
              fieldId,
              config,
              typeName,
            },
            undefined,
            (result) => resolve(result),
          );
        });
      },
      openCodePrefixCreating(): Promise<string | undefined> {
        return new Promise<string | undefined>((resolve) => {
          openForm(
            'createCodePrefix',
            expandedOnlyForm,
            {},
            undefined,
            (result) => resolve(result),
          );
        });
      },
      openSendTestMessage: (subtenantId: number) =>
        openForm(
          'sendTestMessage',
          expandedOnlyForm,
          {
            subtenantId,
          },
          undefined,
        ),
    }),

    [openForm, isAnyImportFormOpened, settingsQuery.data],
  );
  return (
    <FormsContext.Provider value={formsContext}>
      <div className={styles.globalFormsLayout}>
        {Object.values(openedForms).map((form) => {
          const formComponent = formComponents[form.type];
          const FormComponentType = formComponent.componentType();
          const expanded = form.state === 'expanded';
          const hidden = form.isHidden;
          return (
            <div
              key={form.id}
              className={clsx(
                expanded && !hidden ? styles.expandedContainer : '',
              )}
            >
              <PopupWindow
                id={form.id}
                title={form.title || formComponent.title}
                tryClose={tryCloseForm}
                changeState={changeFormState}
                state={form.state}
                isHidden={hidden}
                setIsHidden={setFormIsHidden}
                isBeingCompleted={!!form.isBeingCompleted}
                stateSettings={form.stateSettings}
                isFocused={form.id === focusedFormId}
                isLoading={!!form.isLoading}
                loadingTitle={form.loadingTitle || ''}
                isCloseButtonHidden={form.isCloseButtonHidden}
              >
                <QuerySuspenseErrorWrapper>
                  <FormComponentType
                    formType={form.type}
                    formId={form.id}
                    isExpanded={expanded}
                    tryCompleteForm={tryCompleteForm}
                    closeForm={closeForm}
                    registerFormDirtyStateRetriever={
                      registerFormDirtyStateRetriever
                    }
                    expand={expandForm}
                    loadBlocking={loadBlocking}
                    changeFormTitle={changeFormTitle}
                    setIsCloseButtonHidden={setIsCloseButtonHidden}
                    {...form.additionalFormProps}
                  />
                </QuerySuspenseErrorWrapper>
              </PopupWindow>
            </div>
          );
        })}
      </div>
      {props.children}
    </FormsContext.Provider>
  );
};
